/**
 * Pure state updater functions for the stepper state machine.
 * Each accepts the previous stepsState array and returns the next array.
 */
import { STEP_STATE } from '../lib/constants';

export const buildInitialState = (incoming, prev) => {
  if (!Array.isArray(incoming) || incoming.length === 0) return [];

  return incoming.map((step, sIndex) => {
    const prevStep = prev?.[sIndex];

    const mappedSubsteps =
      step.substeps?.map((sub, subIndex) => {
        const prevSub = prevStep?.substeps?.[subIndex];
        return {
          ...sub,
          active: prevSub?.active ?? sub?.active ?? (sIndex === 0 && subIndex === 0)
        };
      }) ?? [];

    return {
      ...step,
      active: prevStep?.active ?? step?.active ?? sIndex === 0,
      state_status:
        prevStep?.state_status ??
        step?.state_status ??
        (step?.active || sIndex === 0 ? STEP_STATE.EXPANDED : STEP_STATE.COLLAPSED),
      substeps: mappedSubsteps
    };
  });
};

export const expandStepUpdate = (prev, stepCode) =>
  prev.map((step) => {
    if (step.code !== stepCode) return step;
    const isExpanded = step.state_status === STEP_STATE.EXPANDED;
    return {
      ...step,
      state_status: isExpanded ? STEP_STATE.COLLAPSED : STEP_STATE.EXPANDED
    };
  });

export const collapseStepUpdate = (prev, stepCode) =>
  prev.map((step) =>
    step.code === stepCode ? { ...step, state_status: STEP_STATE.COLLAPSED } : step
  );

export const activateStepUpdate = (prev, stepCode) => {
  const hasTargetStep = prev.some((step) => step.code === stepCode);
  if (!hasTargetStep) return prev;
  const currentExpanded = prev.find((s) => s.state_status === STEP_STATE.EXPANDED)?.code;
  if (currentExpanded === stepCode) return prev;

  return prev.map((step) => {
    const isTarget = step.code === stepCode;
    const nextState = isTarget ? STEP_STATE.EXPANDED : STEP_STATE.COLLAPSED;

    const substeps = Array.isArray(step.substeps) ? step.substeps : [];
    const updatedSubsteps = substeps.map((sub, idx) => ({
      ...sub,
      active: isTarget ? idx === 0 : false
    }));

    return {
      ...step,
      active: isTarget,
      state_status: nextState,
      ...(Array.isArray(step.substeps) ? { substeps: updatedSubsteps } : {})
    };
  });
};

export const navigateToSubstepUpdate = (prev, substepCode) =>
  prev.some((step) => step.substeps?.some((s) => s.code === substepCode))
    ? prev.map((step) => {
        const contains = step.substeps?.some((s) => s.code === substepCode);
        const nextStatus = contains
          ? STEP_STATE.EXPANDED
          : step.state_status === STEP_STATE.EXPANDED
            ? STEP_STATE.COLLAPSED
            : step.state_status;

        return {
          ...step,
          active: contains,
          state_status: nextStatus,
          substeps: step.substeps?.map((s) => ({ ...s, active: s.code === substepCode })) ?? []
        };
      })
    : prev;

export const navigateToStepUpdate = (prev, stepCode) =>
  prev.some((step) => step.code === stepCode)
    ? prev.map((step) => {
        const isTarget = step.code === stepCode;
        const nextState = isTarget ? STEP_STATE.EXPANDED : STEP_STATE.COLLAPSED;

        const substeps = Array.isArray(step.substeps) ? step.substeps : [];
        const updatedSubsteps = substeps.map((sub, idx) => ({
          ...sub,
          active: isTarget ? idx === 0 : false
        }));

        return {
          ...step,
          active: isTarget,
          state_status: nextState,
          ...(Array.isArray(step.substeps) ? { substeps: updatedSubsteps } : {})
        };
      })
    : prev;
